import { useState, useEffect, useRef } from 'react'
import { clsx } from 'clsx'
import { useThemeStore } from '@/store/theme'
import { builtInThemes } from '@/themes'

interface ThemeSwitcherProps {
  collapsed: boolean
}

export function ThemeSwitcher({ collapsed }: ThemeSwitcherProps) {
  const { activeThemeId, customThemes, setActiveTheme, addCustomTheme, removeCustomTheme } =
    useThemeStore()
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [newThemeName, setNewThemeName] = useState('')
  const [newThemeCSS, setNewThemeCSS] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Inject/remove CSS override
  useEffect(() => {
    const STYLE_ID = 'admin-theme-override'
    let existingTag = document.getElementById(STYLE_ID)

    const builtIn = builtInThemes.find((t) => t.id === activeThemeId)
    const custom = customThemes.find((t) => t.id === activeThemeId)
    const css = builtIn?.css || custom?.css || ''

    if (!css) {
      existingTag?.remove()
      return
    }

    if (!existingTag) {
      existingTag = document.createElement('style')
      existingTag.id = STYLE_ID
      document.head.appendChild(existingTag)
    }
    existingTag.textContent = css
  }, [activeThemeId, customThemes])

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false)
      }
    }
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [dropdownOpen])

  const activeTheme =
    builtInThemes.find((t) => t.id === activeThemeId) ||
    customThemes.find((t) => t.id === activeThemeId)

  const allThemes = [
    ...builtInThemes,
    ...customThemes.map((t) => ({ ...t, builtIn: false, description: 'Custom theme' })),
  ]

  function handleSaveCustom() {
    if (newThemeName.trim() && newThemeCSS.trim()) {
      addCustomTheme(newThemeName.trim(), newThemeCSS.trim())
      setNewThemeName('')
      setNewThemeCSS('')
      setShowAddDialog(false)
    }
  }

  return (
    <div ref={dropdownRef} className="relative border-t border-slate-700">
      {/* Trigger button */}
      <button
        onClick={() => setDropdownOpen(!dropdownOpen)}
        className={clsx(
          'w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-300 hover:bg-slate-700 hover:text-white transition-colors',
          collapsed && 'justify-center px-2',
        )}
        title={collapsed ? `Theme: ${activeTheme?.name || 'Default'}` : undefined}
      >
        <i className="pi pi-palette text-base" />
        {!collapsed && (
          <>
            <span className="flex-1 text-left">
              <span className="block text-xs text-slate-500">Theme</span>
              <span className="block truncate">{activeTheme?.name || 'Default'}</span>
            </span>
            <i className={clsx('pi text-xs', dropdownOpen ? 'pi-chevron-down' : 'pi-chevron-up')} />
          </>
        )}
      </button>

      {/* Dropdown menu */}
      {dropdownOpen && (
        <div
          className={clsx(
            'absolute bottom-full mb-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl z-50 max-h-72 overflow-y-auto',
            collapsed ? 'left-full ml-1 w-56' : 'left-2 right-2',
          )}
        >
          {allThemes.map((theme) => (
            <div
              key={theme.id}
              className="flex items-center group"
            >
              <button
                onClick={() => {
                  setActiveTheme(theme.id)
                  setDropdownOpen(false)
                }}
                className={clsx(
                  'flex-1 flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors',
                  theme.id === activeThemeId
                    ? 'text-blue-400 bg-slate-700'
                    : 'text-slate-300 hover:bg-slate-700 hover:text-white',
                )}
              >
                {theme.id === activeThemeId ? (
                  <i className="pi pi-check text-xs text-blue-400" />
                ) : (
                  <span className="w-3" />
                )}
                <span className="truncate">{theme.name}</span>
              </button>
              {!theme.builtIn && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    removeCustomTheme(theme.id)
                  }}
                  className="px-2 py-2 text-slate-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Delete custom theme"
                >
                  <i className="pi pi-trash text-xs" />
                </button>
              )}
            </div>
          ))}

          {/* Add Custom CSS option */}
          <div className="border-t border-slate-700">
            <button
              onClick={() => {
                setShowAddDialog(true)
                setDropdownOpen(false)
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-400 hover:bg-slate-700 hover:text-white transition-colors"
            >
              <i className="pi pi-plus text-xs" />
              <span>Add Custom CSS...</span>
            </button>
          </div>
        </div>
      )}

      {/* Add Custom CSS Dialog */}
      {showAddDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-800">Add Custom CSS Theme</h3>
              <button
                onClick={() => setShowAddDialog(false)}
                className="p-1 rounded hover:bg-slate-100 text-slate-400"
              >
                <i className="pi pi-times" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Theme Name
                </label>
                <input
                  type="text"
                  value={newThemeName}
                  onChange={(e) => setNewThemeName(e.target.value)}
                  placeholder="My Custom Theme"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  CSS Content
                </label>
                <textarea
                  value={newThemeCSS}
                  onChange={(e) => setNewThemeCSS(e.target.value)}
                  placeholder="Paste your CSS here..."
                  rows={10}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-200">
              <button
                onClick={() => {
                  setShowAddDialog(false)
                  setNewThemeName('')
                  setNewThemeCSS('')
                }}
                className="px-4 py-2 text-sm rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCustom}
                disabled={!newThemeName.trim() || !newThemeCSS.trim()}
                className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save Theme
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
